import React, { useState, useEffect } from 'react';
import { MessageCircle, ChevronDown, X, Send, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';

const DEFAULT_TEMPLATES = [
  { id: 'default-1', name: 'Primer contacto', message: 'Hola, [Nombre del Cliente]. Buenas tardes. Soy [Nombre del Comercial] de DataLab. Veo que estás interesado en nuestros servicios, ¿cómo podemos ayudarte?' },
  { id: 'default-2', name: 'Seguimiento', message: 'Hola [Nombre del Cliente], soy [Nombre del Comercial] de DataLab. Quería hacer seguimiento a nuestra conversación anterior. ¿Tienes alguna pregunta o podemos agendar una reunión?' },
  { id: 'default-3', name: 'Propuesta enviada', message: 'Hola [Nombre del Cliente], soy [Nombre del Comercial] de DataLab. Ya te enviamos la propuesta al correo. ¿Pudiste revisarla? Estoy atento a tus comentarios.' },
  { id: 'default-4', name: 'Recordatorio reunión', message: 'Hola [Nombre del Cliente], te recuerdo que tenemos reunión programada. ¿Confirmas tu asistencia? Quedo atento.' },
  { id: 'default-5', name: 'Agradecimiento', message: 'Hola [Nombre del Cliente], soy [Nombre del Comercial] de DataLab. Gracias por confiar en nosotros. Si necesitas algo no dudes en escribirme.' },
];

function cleanPhone(phone) {
  if (!phone) return '';
  let cleaned = phone.replace(/[^0-9+]/g, '');
  if (cleaned.startsWith('+')) return cleaned.slice(1);
  if (cleaned.startsWith('57') && cleaned.length >= 12) return cleaned;
  if (cleaned.length === 10) return '57' + cleaned;
  return cleaned;
}

function replaceVars(template, lead, currentUser) {
  return template
    .replace(/\[Nombre del Cliente\]/g, lead?.name || '')
    .replace(/\[Nombre del Comercial\]/g, currentUser?.name || '')
    .replace(/\[Email del Cliente\]/g, lead?.email || '')
    .replace(/\[Telefono del Cliente\]/g, lead?.phone || '')
    .replace(/\[Empresa\]/g, lead?.company || '')
    .replace(/\[Nombre del Servicio\]/g, lead?.requestedServices?.[0] || 'nuestros servicios');
}

export default function WhatsAppButton({ lead }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [editedMessage, setEditedMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    api.getWhatsAppTemplates().then(t => {
      setTemplates(t.length > 0 ? t : DEFAULT_TEMPLATES);
    }).catch(() => setTemplates(DEFAULT_TEMPLATES));
  }, []);

  useEffect(() => {
    if (templates.length > 0) {
      setEditedMessage(replaceVars(templates[selectedIdx]?.message || '', lead, currentUser));
    }
  }, [selectedIdx, templates, lead]);

  const phone = cleanPhone(lead?.phone);

  if (!phone) return null;

  const handleSend = () => {
    const encoded = encodeURIComponent(editedMessage);
    window.open(`https://wa.me/${phone}?text=${encoded}`, '_blank');
    setOpen(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editedMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Mensaje copiado');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors shadow-sm"
        data-testid="whatsapp-btn"
      >
        <MessageCircle className="w-4 h-4" />
        WhatsApp
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[420px] bg-white rounded-xl border border-slate-200 shadow-xl z-50" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-green-500 rounded-t-xl">
            <div className="flex items-center gap-2 text-white">
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold text-sm">Enviar WhatsApp a {lead?.name}</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Template selector */}
          <div className="px-4 pt-3 pb-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Plantilla</label>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((t, i) => (
                <button key={t.id} onClick={() => setSelectedIdx(i)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedIdx === i ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'}`}>
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          {/* Editable message */}
          <div className="px-4 py-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Mensaje (editable)</label>
            <textarea
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              data-testid="whatsapp-message"
            />
            <p className="text-[10px] text-slate-400 mt-1">Número: +{phone}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-100">
            <button onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copiado' : 'Copiar'}
            </button>
            <button onClick={handleSend}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors">
              <Send className="w-4 h-4" />
              Enviar por WhatsApp
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
